import { Link } from "react-router-dom";
import { Reveal } from "./Reveal";
import { useLanguage } from "@/lib/LanguageContext";
import { useRelayLive } from "@/lib/useRelayLive";
import { ArrowUpRight, Activity } from "lucide-react";

export const InfrastructureTeaser = () => {
  const { t } = useLanguage();
  const i = t.infrastructureTeaser;
  const relay = useRelayLive();
  const isSyncing = !!relay?.isSyncing;

  return (
    <section
      id="infrastructure-teaser"
      data-testid="infrastructure-teaser-section"
      className="mc-section bg-white relative"
    >
      <div className="mc-container">
        <div className="grid grid-cols-12 gap-x-8 mb-14 md:mb-20">
          <div className="col-span-12 md:col-span-4">
            <div className="eyebrow">{i.eyebrow}</div>
          </div>
          <div className="col-span-12 md:col-span-8">
            <Reveal>
              <h2 className="font-display text-[34px] md:text-[52px] leading-[1.05] tracking-tight text-[color:var(--mc-secondary)] font-light">
                {i.headingA}{" "}
                <span className="italic text-[color:var(--mc-primary)]">{i.headingB}</span>
              </h2>
            </Reveal>
            <Reveal delay={120}>
              <p className="mt-8 max-w-[640px] text-[15.5px] leading-relaxed text-[color:var(--mc-muted)]">
                {i.body}
              </p>
            </Reveal>
          </div>
        </div>

        <div className="grid grid-cols-12 gap-x-8 gap-y-10 border-t border-[color:var(--mc-line)] pt-10 md:pt-14">
          <div className="col-span-12 md:col-span-8">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-0 border border-[color:var(--mc-line)]">
              <StatCell
                label={i.stats.status}
                value={i.stats.statusValue}
                testid="teaser-stat-status"
                indicator={
                  <span
                    aria-hidden="true"
                    className={`inline-block h-1.5 w-1.5 rounded-full ${
                      isSyncing ? "bg-amber-500 animate-pulse" : "bg-emerald-500"
                    }`}
                  />
                }
              />
              <StatCell label={i.stats.region} value={i.stats.regionValue} testid="teaser-stat-region" />
              <StatCell
                label={i.stats.network}
                value={i.stats.networkValue}
                testid="teaser-stat-network"
                last
              />
            </div>
          </div>
          <div className="col-span-12 md:col-span-4 flex md:justify-end md:items-end">
            <Link
              to="/midnight-relay"
              data-testid="teaser-cta-relay"
              aria-label={i.ctaAria}
              className="group inline-flex items-center gap-2 mc-btn mc-btn-primary self-start md:self-end"
            >
              <Activity size={15} strokeWidth={1.75} />
              <span>{i.cta}</span>
              <ArrowUpRight
                size={15}
                strokeWidth={1.75}
                className="transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
              />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

const StatCell = ({ label, value, indicator, last, testid }) => (
  <div
    data-testid={testid}
    className={`p-6 lg:p-7 flex flex-col gap-2 border-b sm:border-b-0 sm:border-r border-[color:var(--mc-line)] ${
      last ? "sm:border-r-0" : ""
    }`}
  >
    <div className="font-mono-tab text-[10.5px] uppercase tracking-[0.22em] text-[color:var(--mc-muted)]">
      {label}
    </div>
    <div className="flex items-center gap-2 text-[15px] text-[color:var(--mc-secondary)]">
      {indicator}
      <span>{value}</span>
    </div>
  </div>
);
